/**
 * Block renderer for public site - NOS-29 / NOS-151.
 *
 * Thin dispatcher: resolves section content + _styles, then renders the block
 * via the per-type module registry in src/components/blocks/render/. Extra
 * elements (section.content.blocks) render through GenericExtraBlock, ordered
 * by content.element_order.
 */

import type { ReactElement } from "react"
import type { OrgCustomizations } from "@/templates/types"
import type { Organization, PageSection } from "@/templates/types"
import { BLOCK_COMPONENTS } from "@/components/blocks/render"
import {
  GenericExtraBlock,
  resolveBlockStyles,
  sanitizeContentForPublic,
} from "@/components/blocks/render/shared"
import type { BlockType } from "./blocks/types"

export interface BlockRendererProps {
  section: PageSection
  customizations: OrgCustomizations
  org?: Organization | null
  /** When true, render sections even if is_visible is false (builder context panel) */
  showHidden?: boolean
  /** When true, hero uses minHeight 380 and no overlay (avoids double gradient in builder) */
  inBuilder?: boolean
  /** NOS-236: whether this section renders on the homepage (hero scroll-arrow default). */
  isHomePage?: boolean
}

export function BlockRenderer({
  section,
  customizations,
  org,
  showHidden = false,
  inBuilder = false,
  isHomePage,
}: BlockRendererProps) {
  if (section.is_visible === false && !showHidden) return null
  const type = section.section_type as BlockType
  const rawContent = (section.content as Record<string, unknown>) ?? null
  const content = inBuilder ? rawContent : sanitizeContentForPublic(rawContent) ?? rawContent
  const resolved = resolveBlockStyles(content)
  const styleOverrides = resolved.hasOverrides
    ? { className: resolved.sectionClassName, style: resolved.sectionStyle }
    : undefined

  const extraBlocks = (content?.blocks as Array<{ id: string; type: string; content: Record<string, unknown> }> | undefined) ?? []

  function renderMain(): ReactElement | null {
    const Component = BLOCK_COMPONENTS[type]
    if (!Component) return null
    return (
      <Component
        content={content}
        customizations={customizations}
        org={org}
        inBuilder={inBuilder}
        styleOverrides={styleOverrides}
        resolved={resolved}
        sectionId={section.id}
        isHomePage={isHomePage}
      />
    )
  }

  const main = renderMain()
  const sectionClass = (content?._section_class as string | undefined)?.trim()
  // NOS-250: layout-inert parity marker (display:contents) — the Astro
  // renderer emits the same wrapper, so the cross-renderer visual regression
  // suite pairs sections by id. Blocks that render null stay null (unknown
  // types, empty content). Removed with the SPA public routes (M22 exit).
  const wrappedMain = main ? (
    <div
      className={sectionClass || undefined}
      style={sectionClass ? undefined : { display: "contents" }}
      data-nos-section-type={type}
      data-nos-section-id={section.id}
    >
      {main}
    </div>
  ) : null

  if (!extraBlocks.length) return wrappedMain

  const extraBlocksEl = (
    <div className="px-4 sm:px-6 md:px-8 pb-4 space-y-3">
      {extraBlocks
        .filter((block) => (block as Record<string, unknown>).visible !== false)
        .map((block) => (
          <GenericExtraBlock key={block.id} block={block} />
        ))}
    </div>
  )

  // Respect element_order: if "blocks" comes before native content, render extra blocks first
  const elementOrder = (content?.element_order as string[] | undefined) ?? []
  const blocksIdx = elementOrder.indexOf("blocks")
  const firstNativeIdx = elementOrder.findIndex((k) => k !== "blocks")
  const blocksFirst = blocksIdx !== -1 && (firstNativeIdx === -1 || blocksIdx < firstNativeIdx)

  return blocksFirst
    ? <>{extraBlocksEl}{wrappedMain}</>
    : <>{wrappedMain}{extraBlocksEl}</>
}
